import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";

import {
  CheckCircle,
  Build,
  Inventory2,
  AssignmentInd,
  ErrorOutline,
} from "@mui/icons-material";

import {
  TicketTimeline as TicketTimelineType,
} from "../types";

interface TicketTimelineProps {
  open: boolean;

  timeline: TicketTimelineType[];

  ticketNumber?: string;

  onClose: () => void;
}

const getTimelineIcon = (
  status: string
) => {
  switch (status) {
    case "RAISED":
      return <ErrorOutline />;

    case "ASSIGNED":
      return <AssignmentInd />;

    case "ISSUED":
    case "SPARE_ISSUED":
      return <Inventory2 />;

    case "RETURN_PENDING":
    case "RETURN_VERIFIED":
      return <Inventory2 />;

    case "IN_PROGRESS":
      return <Build />;

    case "COMPLETED":
    case "CLOSED":
      return <CheckCircle />;

    default:
      return <CheckCircle />;
  }
};

const getTimelineColor = (
  status: string
):
  | "primary"
  | "success"
  | "warning"
  | "error"
  | "grey" => {
  switch (status) {
    case "RAISED":
      return "error";

    case "ASSIGNED":
      return "primary";

    case "ISSUED":
    case "SPARE_ISSUED":
      return "warning";

    case "RETURN_PENDING":
      return "warning";

    case "RETURN_VERIFIED":
      return "success";

    case "IN_PROGRESS":
      return "primary";

    case "COMPLETED":
    case "CLOSED":
      return "success";

    default:
      return "grey";
  }
};

const TicketTimeline = ({
  open,
  timeline,
  ticketNumber,
  onClose,
}: TicketTimelineProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">
            Ticket Timeline
          </Typography>

          <IconButton
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        {ticketNumber && (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Ticket :
            {" "}
            {ticketNumber}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Timeline position="right">
          {timeline.map(
            (event, index) => (
              <TimelineItem
                key={event.id}
              >
                <TimelineSeparator>
                  <TimelineDot
                    color={getTimelineColor(
                      event.status
                    )}
                  >
                    {getTimelineIcon(
                      event.status
                    )}
                  </TimelineDot>

                  {index !==
                    timeline.length -
                      1 && (
                    <TimelineConnector />
                  )}
                </TimelineSeparator>

                <TimelineContent>
                  <Stack spacing={0.5}>
                    <Typography
                      fontWeight={600}
                    >
                      {event.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {event.description}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {new Date(
                        event.createdAt
                      ).toLocaleString()}
                    </Typography>
                                        {event.createdBy && (
                      <Typography
                        variant="caption"
                        color="primary"
                      >
                        By: {event.createdBy}
                      </Typography>
                    )}

                    {event.remarks && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          fontStyle: "italic",
                        }}
                      >
                        "{event.remarks}"
                      </Typography>
                    )}
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            )
          )}

          {timeline.length === 0 && (
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot color="grey" />
              </TimelineSeparator>

              <TimelineContent>
                <Typography
                  color="text.secondary"
                >
                  No timeline history available.
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
        </Timeline>
      </DialogContent>
    </Dialog>
  );
};

export default TicketTimeline;
